import { FISCAL_CALENDAR_NAME, getCustomCalendarGuid, } from '@thoughtspot/custom-calendar-service';
import * as dateUtil from '@thoughtspot/date-format-util';
import { extractCaptionTitleAndLink, getColumnCurrencyInfo, getColumnToFormatPatternType, getDataFormatter, getFormatPattern, getFormattedValueForNumericColumn, getFormatterTypeEnum, getUniqueCurrencyCode, isColumnCurrencyFormatType, isCurrencyColumn, isEffectivelyPercent, isPercentColumn, useMeasureOnXAxisAsOrdinal, } from '@thoughtspot/formatter-service';
import { getCustomCalendarGuidFromColumn, getEffectiveDataType, getEffectiveDateNumDataType, getId, getTimeBucket, hasCustomCalendar, isCalendarGregorianOrFiscal, isColumnValidForNumberFormatting, isDateColumn, isDateNumColumn, isDateTimeColumn, isDoubleColumn, isEffectivelyNumeric, isFloat, isGrowth, isTimeColumn, showDateFinancialYearFormat, } from '@thoughtspot/visualization-column-util';
import _ from 'lodash';
import { getDataRowIndex, getQueryIndex } from '../core/axis-config-util';
import { formatCompositeAxisValue, formatCompositeColumnValue, getBatchSize, getChartType, getColorBySerieName, getFormattingForColumn, getHandleMissingValues, getHighchartCustomisationSettings, getIsYAxisColInLineStackedChart, getLabelForNumericAxis, getQueriesFromChartModel, getRadialColumn, getSeriesColorsMap, getShowDataLabels, getSortIndex, getUserDefinedFormattingForColumn, getVisualizedColumns, getXAxes, getXAxisColumns, getYAxisColumns, isDescendingSort, isExcludeNullValuesSelected, isPrimarySortOnXAxisColumns, isSorted, isTimeSeries, isXAxisDate, isXAxisMeasure, isXAxisOrdinalBased, isYAxisOrdinalBased, } from '../core/chart-model-utils';
import { configOptions } from '../core/chart-type-specification-service';
import { getName } from '../core/column-name-util';
import { getEnvFlags } from '../env';
import { HandleMissingValues, } from '../types';
import { mapArrayToHash } from '../utils';
import { createLogger, LogLevel } from '../utils/logger';
const logger = createLogger('data-util');
export var ShowNullAsType;
(function (ShowNullAsType) {
    ShowNullAsType["ZERO"] = "zero";
    ShowNullAsType["NULL"] = "null";
})(ShowNullAsType || (ShowNullAsType = {}));
export const getDataArray = (vizData) => {
    let hasError = false;
    let hasNoData = false;
    vizData.forEach(dataset => {
        if (!dataset.data) {
            hasError = true;
            hasNoData = true;
        }
        if (dataset.data && !dataset.data.length) {
            hasNoData = true;
        }
    });
    if (hasError || hasNoData) {
        return;
    }
    return vizData.map(dataJson => {
        return {
            info: dataJson,
            data: dataJson.data,
            getData() {
                return this.data;
            },
        };
    });
};
export const containsNegativeValuesOnY = (yAxisColumns, dataRows) => {
    if (!dataRows) {
        return true;
    }
    const yAxisColumnsDataIndexes = yAxisColumns.map(function (yAxisColumn) {
        return getDataRowIndex(yAxisColumn);
    });
    return dataRows.some(function (dataRow) {
        return yAxisColumnsDataIndexes.some(function (dataRowIndex) {
            return dataRow[dataRowIndex] < 0;
        });
    });
};
export const containsAllZeroValuesOnY = (yAxisColumns, dataRows) => {
    if (_.isEmpty(dataRows)) {
        return true;
    }
    const yAxisColumnsDataIndexes = yAxisColumns.map(function (yAxisColumn) {
        return getDataRowIndex(yAxisColumn);
    });
    return dataRows.every(function (dataRow) {
        return yAxisColumnsDataIndexes.every(function (dataRowIndex) {
            return dataRow[dataRowIndex] === 0;
        });
    });
};
export const containsAllNegativeValuesOnSizeColumn = (sizeColumn, dataRows) => {
    if (!dataRows) {
        return true;
    }
    return dataRows.every(function (dataRow) {
        return dataRow[sizeColumn.dataRowIndex] < 0;
    });
};
export const columnHasInfinityValues = (dataModel, column) => dataModel.columnHasInfinityValues[getDataRowIndex(column)];
export const columnHasMinusInfinityValues = (dataModel, column) => dataModel.columnHasMinusInfinityValues[getDataRowIndex(column)];
export const getMaxXValue = (chartModel, dataModel, xAxisColumn) => {
    const queryIndex = getQueryIndex(xAxisColumn);
    const dataRowIndex = getDataRowIndex(xAxisColumn);
    if (!isXAxisMeasure(chartModel) && !isXAxisDate(chartModel)) {
        logger.error('max/min of x axis is supported only when x-axis is a measure/date');
        return Number.POSITIVE_INFINITY;
    }
    return _.get(dataModel.maxColumnValues, `${queryIndex}.${dataRowIndex}`);
};
export const getMinXValue = (chartModel, dataModel, xAxisColumn) => {
    const queryIndex = getQueryIndex(xAxisColumn);
    const dataRowIndex = getDataRowIndex(xAxisColumn);
    if (!isXAxisMeasure(chartModel) && !isXAxisDate(chartModel)) {
        logger.error('max/min of x axis is supported only when x-axis is a measure/date');
        return Number.NEGATIVE_INFINITY;
    }
    return dataModel.minColumnValues[queryIndex][dataRowIndex];
};
export const getMaxYValue = (dataModel, yAxisColumn) => dataModel.getMaxYValue(getQueryIndex(yAxisColumn), getDataRowIndex(yAxisColumn));
export const getMinYValue = (dataModel, yAxisColumn) => dataModel.getMinYValue(getQueryIndex(yAxisColumn), getDataRowIndex(yAxisColumn));
export const getInfinityPlaceholder = (dataModel, column) => dataModel.columnInfinityPlaceholder[getDataRowIndex(column)];
export const getNegativeInfinityPlaceholder = (dataModel, column) => dataModel.columnNegativeInfinityPlaceholder[getDataRowIndex(column)];
export const getTotalRowCount = (chartModel) => parseInt(chartModel.data[0].totalRowCount, 10);
export const getTotalRowsDisplayed = (chartModel) => {
    const totalRowsDisplayed = chartModel.data[0].columnDataLite[0].dataValue.length;
    return totalRowsDisplayed;
};
export const hasCaptionValuesForXAxis = dataModel => {
    const xValueToRawValues = dataModel.xValueToRawValues;
    if (xValueToRawValues) {
        return xValueToRawValues.some(extractCaptionTitleAndLink);
    }
    return false;
};
export const getSamplingRatio = (chartModel) => chartModel.data[0].samplingRatio;
export const getCompletionRatio = (chartModel) => chartModel.data[0].completionRatio;
export const getMetricsForSampledData = (chartModel, maxDataSizeForHighCardinality, defaultBatchSize) => {
    const hardLimit = getBatchSize(chartModel, defaultBatchSize, getChartType(chartModel));
    let totalRowsDisplayed = getTotalRowsDisplayed(chartModel);
    let totalRowCount;
    if (getTotalRowCount(chartModel) > hardLimit) {
        totalRowCount = getTotalRowCount(chartModel);
    }
    else {
        totalRowsDisplayed = getTotalRowCount(chartModel);
        totalRowCount = totalRowsDisplayed;
    }
    if (getSamplingRatio(chartModel) < 1 ||
        getCompletionRatio(chartModel) < 1) {
        if (getBatchSize(chartModel, defaultBatchSize, getChartType(chartModel)) === maxDataSizeForHighCardinality) {
            totalRowsDisplayed = maxDataSizeForHighCardinality;
            totalRowCount = totalRowsDisplayed;
        }
    }
    return {
        totalRowCount,
        totalRowsDisplayed,
    };
};
export const getSeries = (dataModel) => _.get(dataModel, 'series', []);
export const getSerie = (dataModel, serieName) => (dataModel === null || dataModel === void 0 ? void 0 : dataModel._seriesMap) ? dataModel._seriesMap[serieName] : '';
export const getSeriesColor = (chartModel, dataModel, serieName) => {
    const serie = getSerie(dataModel, serieName);
    const seriesColors = getSeriesColorsMap(chartModel);
    const serieColorBySeriesId = getColorBySerieName(seriesColors, (serie === null || serie === void 0 ? void 0 : serie.blinkSeriesId) || (serie === null || serie === void 0 ? void 0 : serie.name));
    const serieColorBySeriesName = getColorBySerieName(seriesColors, serieName);
    const isYAxisColInLineStackedChart = getIsYAxisColInLineStackedChart(chartModel, serie === null || serie === void 0 ? void 0 : serie.valueColumnIdentifier);
    if (isYAxisColInLineStackedChart && serieColorBySeriesName) {
        return serieColorBySeriesName;
    }
    if (serieColorBySeriesId) {
        return serieColorBySeriesId;
    }
    if (serieColorBySeriesName) {
        return serieColorBySeriesName;
    }
    if (serie && serie.color) {
        return serie.color;
    }
    logger.debug(`unable to find color for the label ${serieName}`);
    return ' ';
};
export const getXAxisCardinality = (dataModel) => dataModel.xValueToRawValues.length;
export const getXAxisRawValues = (dataModel) => dataModel.xValueToRawValues;
export const getYAxisRawValues = (dataModel) => dataModel.yValueToRawValues;
export const getQueryBasedYAxisColumns = (dataModel) => _.reduce(dataModel.getQueryDefinitions(), (yCols, chartQueryDefinition) => {
    return [...yCols, ...chartQueryDefinition.yAxisColumns];
}, []);
export const getQueryToColumnMap = (dataModel) => {
    const yAxisColumn = getQueryBasedYAxisColumns(dataModel);
    const queryToColumnMap = [];
    yAxisColumn.forEach(col => {
        if (!queryToColumnMap[col.queryIndex])
            queryToColumnMap[col.queryIndex] = [];
        queryToColumnMap[col.queryIndex].push(col.id);
    });
    return queryToColumnMap;
};
export const getAugmentYaxisRawValue = (dataModel) => dataModel.augmentYValueToRawValues || [];
export const getLegendColumnValues = (dataModel) => dataModel.uniqueLegendValues || [];
export const getMaxRadialValue = (chartModel, dataModel) => {
    const radialColumn = dataModel.getQueryDefinitions()[0].radialColumn;
    return getMaxYValue(dataModel, radialColumn);
};
export const getMinRadialValue = (chartModel, dataModel) => {
    const radialColumn = dataModel.getQueryDefinitions()[0].radialColumn;
    return getMinYValue(dataModel, radialColumn);
};
export const _getXAxisValuesAt = (dataModel, x) => getXAxisRawValues(dataModel)[x];
export const _hasXAxisValuesAt = (dataModel, x) => dataModel.xValueToRawValues.hasOwnProperty(x);
export const getXAxisLabelAt = (chartModel, dataModel, x, allXValuesOnLabels, options, getTranslatedText, filterTooltipColumnsWithTooltipConfig = false) => {
    const xAxis = getXAxes(chartModel)[0];
    const firstXAxisColumn = _.first(getXAxisColumns(chartModel));
    const useMeasureAsOrdinal = useMeasureOnXAxisAsOrdinal(getChartType(chartModel));
    const xAxisColumns = getXAxisColumns(chartModel);
    if (xAxisColumns.length === 1 &&
        isColumnValidForNumberFormatting(firstXAxisColumn)) {
        if (!useMeasureAsOrdinal) {
            return getLabelForNumericAxis(chartModel, x, xAxis.id, allXValuesOnLabels, null);
        }
        if (!_hasXAxisValuesAt(dataModel, x)) {
            return '';
        }
        const realXValues = allXValuesOnLabels &&
            allXValuesOnLabels
                .map(index => {
                if (!_hasXAxisValuesAt(dataModel, index)) {
                    return null;
                }
                return _.first(_getXAxisValuesAt(dataModel, index)).value;
            })
                .compact();
        return getLabelForNumericAxis(chartModel, _.first(_getXAxisValuesAt(dataModel, x)).value, xAxis.id, realXValues, null);
    }
    const isTimeSeriesChart = isTimeSeries(chartModel);
    if (!_.isInteger(x) && !isTimeSeriesChart) {
        return '';
    }
    if (isTimeSeriesChart) {
        let timestampValue;
        const isXAxisOrdinal = isXAxisOrdinalBased(chartModel);
        if (!isXAxisOrdinal) {
            timestampValue = x;
        }
        else {
            if (!_hasXAxisValuesAt(dataModel, x)) {
                return '';
            }
            timestampValue = _.first(_getXAxisValuesAt(dataModel, x)).value;
        }
        const customCalendarMapForColumn = dataModel.getDisplayCustomCalendarMapForColumn(firstXAxisColumn);
        return getDataFormatter(firstXAxisColumn)(timestampValue, {
            ...options,
            displayToCustomCalendarValueMap: customCalendarMapForColumn,
        });
    }
    let xValues = _getXAxisValuesAt(dataModel, x);
    if (_.isUndefined(xValues)) {
        logger.everyN(LogLevel.WARN, 10, 'invalid x-axis value', x);
        return '';
    }
    if (filterTooltipColumnsWithTooltipConfig) {
        const { tooltipConfig } = chartModel.vizProp;
        const { columnIds } = tooltipConfig;
        if (!_.isEmpty(columnIds)) {
            xValues = xValues.filter(item => columnIds.includes(item.columnEffectiveId));
        }
    }
    return formatCompositeAxisValue(chartModel, xValues, dataModel);
};
const getColumnFromQueryDefinition = (columnId, queryDefinitions) => {
    const allColumns = _.flatten(queryDefinitions.map(queryDef => queryDef.getColumnsInDataOrder()));
    return _.find(allColumns, col => getId(col) === columnId);
};
export const getColumnConfig = (answerColumn, chartModel, dataModel) => {
    var _a, _b;
    const id = getId(answerColumn);
    const col = (getColumnFromQueryDefinition(id, dataModel.getQueryDefinitions()) || answerColumn);
    const customCalendarMapForColumn = dataModel.getDisplayCustomCalendarMapForColumn(answerColumn);
    const options = {
        id,
        name: getName(col),
        isEffectivelyNumeric: isEffectivelyNumeric(col),
        isColumnValidForNumberFormatting: isColumnValidForNumberFormatting(col),
        userDefinedFormat: getUserDefinedFormattingForColumn(chartModel, id),
        format: getFormattingForColumn(chartModel, id),
        formatPattern: getFormatPattern(col),
        isDouble: isDoubleColumn(col),
        isFloat: isFloat(col),
        isPercent: isPercentColumn(col),
        isEffectivelyPercent: isEffectivelyPercent(col),
        isCurrency: isCurrencyColumn(col),
        formatterTypeEnum: getFormatterTypeEnum(col),
        isDateColumn: isDateColumn(col),
        isDateTimeColumn: isDateTimeColumn(col),
        isDateNumColumn: isDateNumColumn(col),
        isTimeColumn: isTimeColumn(col),
        displayToCustomCalendarValueMap: customCalendarMapForColumn,
        isDescendingSort: isDescendingSort(chartModel, col),
        currencyTypeInfo: {
            settingType: (_a = getColumnCurrencyInfo(col)) === null || _a === void 0 ? void 0 : _a.type,
            isoCode: (_b = getColumnCurrencyInfo(col)) === null || _b === void 0 ? void 0 : _b.isoCode,
        },
        timeBucket: getTimeBucket(col),
        effectiveDataType: isDateNumColumn(col)
            ? getEffectiveDateNumDataType(col)
            : getEffectiveDataType(col),
        currencyCode: getUniqueCurrencyCode(col),
        columnToFormatPatternType: getColumnToFormatPatternType(col),
        isColumnToFormatPatternType: isColumnCurrencyFormatType(col),
        isYAxisOrdinalBased: isYAxisOrdinalBased(chartModel),
        dataRowIndex: getDataRowIndex(col),
        isSorted: isSorted(chartModel, col),
        hasCustomCalendar: hasCustomCalendar(col),
        sortIndex: isSorted(chartModel, col) && getSortIndex(chartModel, col),
        isCalendarGregorianOrFiscal: isCalendarGregorianOrFiscal(col),
        columnHasInfinityValues: columnHasInfinityValues(dataModel, col),
        columnHasMinusInfinityValues: columnHasMinusInfinityValues(dataModel, col),
        columnInfinityPlaceholder: getInfinityPlaceholder(dataModel, col),
        columnNegativeInfinityPlaceholder: getNegativeInfinityPlaceholder(dataModel, col),
        showFinancialFormat: showDateFinancialYearFormat(col),
        showDataLabels: getShowDataLabels(chartModel, id) === true,
        isGrowth: isGrowth(col),
    };
    let formatPattern = getFormatPattern(col);
    if (isDateColumn(col) || isDateNumColumn(col) || isTimeColumn(col)) {
        const isDateTimeCol = isDateTimeColumn(col);
        if (!formatPattern) {
            if (isDateTimeCol) {
                formatPattern = getEnvFlags().includeMilliSeconds
                    ? dateUtil.dateFormatPresets.DATETIME_SHORT_WITH_MILLIS
                    : dateUtil.dateFormatPresets.DATETIME_SHORT_WITH_SECONDS;
            }
            else if (isTimeColumn(col)) {
                formatPattern = 'HH:mm:ss';
            }
            else {
                formatPattern = dateUtil.dateFormatPresets.DATE_SHORT;
            }
        }
        const customCalendarOverridesFiscalOffset = hasCustomCalendar(col) &&
            getCustomCalendarGuidFromColumn(col) !==
                getCustomCalendarGuid(FISCAL_CALENDAR_NAME);
        Object.assign(options, {
            formatPattern,
            hasCustomCalendar: hasCustomCalendar(col),
            displayToCustomCalendarValueMap: customCalendarMapForColumn,
            customCalendarOverridesFiscalOffset,
        });
    }
    const radialColumn = getRadialColumn(chartModel);
    if (radialColumn && getId(radialColumn) === id) {
        Object.assign(options, {
            maxRadialValue: getMaxRadialValue(chartModel, dataModel),
            minRadialValue: getMinRadialValue(chartModel, dataModel),
        });
    }
    return options;
};
export const getColumnDataValues = (columnId, chartModel) => {
    let column;
    for (let i = 0; i < chartModel.data.length; i++) {
        column = chartModel.data[i].columnDataLite.find(c => c.columnId === columnId);
        if (column) {
            return column.dataValue;
        }
    }
    return column;
};
export const hasCaptionValuesForYAxis = (dataModel, colIdx) => {
    const yValueToRawValues = dataModel.yValueToRawValues[colIdx];
    return (yValueToRawValues &&
        _.some(yValueToRawValues, extractCaptionTitleAndLink));
};
export const getTooltipConfig = (chartModel) => {
    var _a, _b, _c;
    let tooltipConfig = getEnvFlags().isTooltipCustomizationEnabled
        ? (_a = chartModel.vizProp) === null || _a === void 0 ? void 0 : _a.tooltipConfig
        : null;
    if (tooltipConfig) {
        const isColumnIdsEmpty = ((_b = tooltipConfig === null || tooltipConfig === void 0 ? void 0 : tooltipConfig.columnIds) === null || _b === void 0 ? void 0 : _b.length) === 0;
        const advancedProperties = getHighchartCustomisationSettings({
            vizProp: chartModel.vizProp,
        });
        const isAdvancedPropsFlagSet = ((_c = advancedProperties === null || advancedProperties === void 0 ? void 0 : advancedProperties.tooltip) === null || _c === void 0 ? void 0 : _c.enabled) !== undefined;
        if (isColumnIdsEmpty && !isAdvancedPropsFlagSet) {
            tooltipConfig = null;
        }
    }
    return tooltipConfig;
};
export const getAllColumnsConfig = (chartModel, dataModel) => {
    const columns = getVisualizedColumns(chartModel);
    return columns.reduce((acc, col, key) => {
        const columnConfig = getColumnConfig(col, chartModel, dataModel);
        acc[columnConfig.id] = columnConfig;
        return acc;
    }, {});
};
export const getXAxisColumnLabelAt = (chartModel, dataModel, x) => {
    const firstXAxisColumn = getXAxisColumns(chartModel)[0];
    const useMeasureAsOrdinal = useMeasureOnXAxisAsOrdinal(getChartType(chartModel));
    const xAxisColumns = getXAxisColumns(chartModel);
    if (xAxisColumns.length === 1 &&
        isColumnValidForNumberFormatting(firstXAxisColumn)) {
        const value = !useMeasureAsOrdinal
            ? x
            : _getXAxisValuesAt(chartModel, x)[0].value;
        return getFormattedValueForNumericColumn(value, firstXAxisColumn);
    }
    const timeSeries = isTimeSeries(chartModel);
    if (!_.isInteger(x) && !timeSeries) {
        return '';
    }
    if (timeSeries) {
        let timestampValue;
        const isXOrdinalBased = isXAxisOrdinalBased(chartModel);
        if (!isXOrdinalBased) {
            timestampValue = x;
        }
        else {
            if (!_hasXAxisValuesAt(chartModel, x)) {
                return '';
            }
            timestampValue = _getXAxisValuesAt(dataModel, x)[0].value;
        }
        return getDataFormatter(firstXAxisColumn)(timestampValue);
    }
    const xValues = _getXAxisValuesAt(chartModel, x);
    if (_.isUndefined(xValues)) {
        logger.everyN(LogLevel.WARN, 10, 'invalid x-axis value', x);
        return '';
    }
    return formatCompositeColumnValue(chartModel, xValues, dataModel);
};
export function getDefaultQueryData(dataModel) {
    const dataArray = dataModel.getDataArray();
    const defaultQueryData = dataArray && dataArray[0];
    return defaultQueryData && defaultQueryData.getData();
}
export const getCardinalityData = metaInfo => metaInfo.colCardinality;
export const getLegendCardinality = (chartModel, dataModel) => {
    const chartConfigOptions = configOptions(getChartType(chartModel), chartModel);
    const displayYValuesWithLegendSeries = chartConfigOptions.displayYValuesWithLegendSeries;
    const yAxisColumns = getYAxisColumns(chartModel);
    const uniqueLegendValues = (dataModel === null || dataModel === void 0 ? void 0 : dataModel.uniqueLegendValues) || [];
    if (yAxisColumns.length > 1) {
        if (displayYValuesWithLegendSeries) {
            const legendSeriesCount = uniqueLegendValues.length;
            return legendSeriesCount + yAxisColumns.length - 1;
        }
        return yAxisColumns.length;
    }
    return uniqueLegendValues.length;
};
export const transformData = (dataArray = []) => {
    const transform = (columnData = []) => {
        var _a, _b;
        const tableData = [];
        const numRows = (_b = (_a = columnData[0]) === null || _a === void 0 ? void 0 : _a.dataValue) === null || _b === void 0 ? void 0 : _b.length;
        for (let i = 0; i < numRows; i++) {
            const row = [];
            for (let j = 0; j < columnData.length; j++) {
                const dataValue = columnData[j].dataValue;
                row.push(dataValue[i]);
            }
            tableData.push(row);
        }
        return { data: tableData };
    };
    return _.map(dataArray, data => {
        return transform(data.columnDataLite);
    });
};
export const generateQueryFromAxisConfig = (axisConfigInput, queryIndex = 0) => {
    const axisConfig = _.cloneDeep(axisConfigInput);
    const x = axisConfig.x;
    const y = axisConfig.y;
    const color = axisConfig.color;
    const size = axisConfig.size;
    const category = axisConfig.category;
    const sort = axisConfig.sort;
    const measureValueColumns = axisConfig.measureValues;
    const columnsInDataOrder = _.concat(color || [], category || [], x || [], y || [], size || [], sort || [], measureValueColumns || []);
    _.each(columnsInDataOrder, (col, dataRowIndex) => {
        col.queryIndex = queryIndex;
        col.dataRowIndex = dataRowIndex;
    });
    const columnsGroupedByType = {
        x: x || [],
        y: y || [],
        color: color || [],
        category: category || [],
        size: size ? [size] : [],
        sort: sort || [],
        measureValues: measureValueColumns || [],
    };
    return {
        getColumnsInDataOrder: () => columnsInDataOrder,
        getColumnsGroupedByType: () => columnsGroupedByType,
        xAxisColumns: x,
        yAxisColumns: y,
        legendColumns: color,
        radialColumn: size,
        categoryColumns: category,
        sortColumns: sort,
        measureValueColumns,
    };
};
export const generateQueryDefinitions = (chartModel) => {
    const queryDefinitions = [];
    _.map(getQueriesFromChartModel(chartModel), (axisConfig, queryIndex) => {
        queryDefinitions.push(generateQueryFromAxisConfig(axisConfig, queryIndex));
    });
    return queryDefinitions;
};
export const getDisplayValue = (value, getTranslatedText) => {
    if (value === null) {
        return getTranslatedText('NULL_VALUE_PLACEHOLDER_LABEL', {
            skipInterpolation: true,
        });
    }
    if (value === '') {
        return getTranslatedText('EMPTY_VALUE_PLACEHOLDER_LABEL', {
            skipInterpolation: true,
        });
    }
    if (typeof value === 'number') {
        return value.toString();
    }
    if (typeof value === 'object') {
        logger.warn('getDisplayValue called with object', value);
    }
    return value;
};
export const convertNullToShiftedDateTime = (series, uniqueXAxisValues, chartModel) => {
    const xAxisColumns = getXAxisColumns(chartModel);
    const timeBucket = getTimeBucket(xAxisColumns[0]);
    let intervalMillis = dateUtil.getIntervalMillisForTimeBucket(timeBucket);
    if (intervalMillis <
        dateUtil.getIntervalMillisForTimeBucket(dateUtil.timeBuckets.DAILY)) {
        intervalMillis = dateUtil.getIntervalMillisForTimeBucket(dateUtil.timeBuckets.DAILY);
    }
    return _.cloneDeep(series).map(serieObj => {
        var _a;
        const serie = _.cloneDeep(serieObj);
        if (serie.data[0] && serie.data[0].x === null)
            if ((_a = uniqueXAxisValues[1]) === null || _a === void 0 ? void 0 : _a[0]) {
                const minDateTime = uniqueXAxisValues[1][0].value;
                serie.data[0].x = new Date(minDateTime - intervalMillis).getTime();
            }
            else {
                serie.data[0].x = intervalMillis;
            }
        return serie;
    });
};
export const getDataRowIndicesAllowingNullsOrZeroes = (allIncludedColumns, chartModel, showNullAsType, isHandleMissingValuesEnabled, sortedOnX) => {
    const dataRowColumnsAllowingNulls = allIncludedColumns.filter(column => {
        const nullToBeHandledAs = getHandleMissingValues(chartModel, column.id);
        if (nullToBeHandledAs !== HandleMissingValues.ShowAsZero &&
            showNullAsType === ShowNullAsType.ZERO) {
            return false;
        }
        if (isEffectivelyNumeric(column)) {
            if (getYAxisColumns(chartModel).some(col => col.id === column.id)) {
                return ([
                    HandleMissingValues.ShowAsZero,
                    HandleMissingValues.ShowGap,
                ].includes(nullToBeHandledAs) ||
                    isYAxisOrdinalBased(chartModel));
            }
            if (getXAxisColumns(chartModel).some(col => col.id === column.id)) {
                return !sortedOnX;
            }
            return false;
        }
        if (showNullAsType === ShowNullAsType.ZERO) {
            return false;
        }
        if (isHandleMissingValuesEnabled &&
            isDateColumn(column) &&
            !isExcludeNullValuesSelected(chartModel, column.id)) {
            return true;
        }
        if (isDateColumn(column)) {
            return !sortedOnX;
        }
        return true;
    });
    return mapArrayToHash(dataRowColumnsAllowingNulls, getDataRowIndex, true);
};
export const updateNullToZero = (dataArray, allIncludedColumns, chartModel, isHandleMissingValuesEnabled, baseQueryIndex) => {
    const transformedData = dataArray;
    if (_.isNil(transformedData)) {
        return dataArray;
    }
    const allIncludedDataRowIndices = _.map(allIncludedColumns, getDataRowIndex);
    const sortedOnX = isPrimarySortOnXAxisColumns(chartModel, getXAxisColumns(chartModel), getYAxisColumns(chartModel));
    const dataRowIndicesAllowingconversion = getDataRowIndicesAllowingNullsOrZeroes(allIncludedColumns, chartModel, ShowNullAsType.ZERO, isHandleMissingValuesEnabled, sortedOnX);
    const baseQueryData = transformedData[baseQueryIndex];
    const data = baseQueryData.getData();
    transformedData[baseQueryIndex].data = data.map((dataRowList) => {
        const dataRow = _.cloneDeep(dataRowList);
        _.forEach(allIncludedDataRowIndices, dataRowIndex => {
            if (dataRowIndex > 0 && dataRowIndex < dataRow.length) {
                const value = dataRow[dataRowIndex];
                if (value === null &&
                    _.has(dataRowIndicesAllowingconversion, dataRowIndex)) {
                    dataRow[dataRowIndex] = 0;
                }
            }
        });
        return dataRow;
    });
    return transformedData;
};
//# sourceMappingURL=data-util.js.map